import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Product, BulkImportResult } from '../types';

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (products: Product[]) => void;
}

const BulkImportModal = ({ isOpen, onClose, onImport }: BulkImportModalProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (selectedFile: File) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.csv')) {
      alert('Por favor, selecione um arquivo CSV ou Excel válido');
      return;
    }

    setFile(selectedFile);
    setResult(null);
  };

  const parseCSV = (text: string): Product[] => {
    const lines = text.split('\n').filter(line => line.trim());
    const products: Product[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      
      if (values.length >= 5) {
        products.push({
          id: `import-${Date.now()}-${i}`,
          code: values[0] || '',
          description: values[1] || '',
          ncm: values[2] || '',
          cest: values[3] || '',
          quantity: parseInt(values[4]) || 1
        });
      }
    }
    
    return products;
  };

  const processImport = async () => {
    if (!file) return;

    setImporting(true);
    
    try {
      const text = await file.text();
      const products = parseCSV(text);
      
      const errors: string[] = [];
      const validProducts = products.filter((product, index) => {
        if (!product.code) {
          errors.push(`Linha ${index + 2}: Código do produto obrigatório`);
          return false;
        }
        if (!product.description) {
          errors.push(`Linha ${index + 2}: Descrição obrigatória`);
          return false;
        }
        if (!product.ncm) {
          errors.push(`Linha ${index + 2}: NCM obrigatório`);
          return false;
        }
        return true;
      });

      setResult({
        success: validProducts.length > 0,
        imported: validProducts.length,
        errors
      });

      if (validProducts.length > 0) {
        onImport(validProducts);
      }
    } catch (error) {
      setResult({
        success: false,
        imported: 0,
        errors: ['Erro ao processar arquivo. Verifique o formato.']
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'Código,Descrição,NCM,CEST,Quantidade\nPROD001,Produto Exemplo,0000.00.00,00.000.00,1';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_produtos.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />
      
      <div
        className="relative bg-card rounded-lg shadow-modal w-full max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-modal-title"
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 id="import-modal-title" className="text-xl font-semibold text-foreground">
            Importação em Massa
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-md transition-colors"
            aria-label="Fechar"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-start gap-3 p-4 bg-accent/10 border border-accent/20 rounded-md">
            <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm text-foreground">
              <p className="font-medium mb-1">Formato do arquivo:</p>
              <p>O arquivo deve conter as colunas: Código, Descrição, NCM, CEST, Quantidade</p>
              <button
                onClick={downloadTemplate}
                className="text-accent hover:underline mt-2 inline-flex items-center gap-1"
              >
                <Icon name="Download" size={14} color="currentColor" />
                Baixar template de exemplo
              </button>
            </div>
          </div>

          <div
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${dragActive ? 'border-primary bg-primary/5' : 'border-border'}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileInput}
              className="hidden"
              aria-label="Selecionar arquivo"
            />
            
            {!file ? (
              <>
                <Icon name="Upload" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">
                  Arraste e solte seu arquivo aqui
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  ou
                </p>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Selecionar Arquivo
                </Button>
                <p className="text-xs text-muted-foreground mt-4">
                  Formatos aceitos: CSV, XLS, XLSX
                </p>
              </>
            ) : (
              <div className="flex items-center justify-center gap-3">
                <Icon name="FileText" size={24} color="var(--color-primary)" />
                <div className="text-left">
                  <p className="text-foreground font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                  aria-label="Remover arquivo"
                >
                  <Icon name="X" size={20} color="var(--color-destructive)" />
                </button>
              </div>
            )}
          </div>

          {result && (
            <div className={`p-4 rounded-md ${result.success ? 'bg-success/10 border border-success/20' : 'bg-error/10 border border-error/20'}`}>
              <div className="flex items-start gap-3">
                <Icon
                  name={result.success ? 'CheckCircle2' : 'AlertCircle'}
                  size={20}
                  color={result.success ? 'var(--color-success)' : 'var(--color-error)'}
                  className="flex-shrink-0 mt-0.5"
                />
                <div className="flex-1">
                  <p className="font-medium text-foreground mb-2">
                    {result.success
                      ? `${result.imported} produtos importados com sucesso`
                      : 'Erro na importação'}
                  </p>
                  {result.errors.length > 0 && (
                    <div className="space-y-1">
                      {result.errors.map((error, index) => (
                        <p key={index} className="text-sm text-foreground">
                          • {error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="default"
            onClick={processImport}
            disabled={!file || importing}
            loading={importing}
            iconName="Upload"
            iconPosition="left"
          >
            Importar Produtos
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BulkImportModal;